'use client';

import { memo, useEffect, useState } from 'react';
import { Input } from '../../input';
import type { FilterStructure } from '../types';

interface NumberInputProps {
    filterOption: FilterStructure;
    value: string;
    setValue: (value: string) => void;
    placeholder: string;
    disabled?: boolean;
    dataTestId?: string;
}

/**
 * Componente para filtros numéricos
 * Utiliza el Input de shadcn con type="number" para campos numéricos
 * Solo aplica el filtro cuando se pierde el foco o se presiona Enter
 * Valida que el valor sea un número válido
 */
const NumberInput = memo(({
    filterOption,
    value,
    setValue,
    placeholder,
    disabled = false,
    dataTestId,
}: NumberInputProps) => {
    const config = filterOption.textConfig;

    // Estado local para el valor del input mientras el usuario escribe
    const [localValue, setLocalValue] = useState(value);

    // Sincronizar estado local cuando cambie el valor externo
    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    // Aplicar el filtro cuando se termine de escribir
    const applyFilter = () => {
        if (localValue !== value) {
            // Validar que sea un número válido antes de aplicar
            const numericValue = localValue.trim();
            if (numericValue === '' || !isNaN(Number(numericValue))) {
                setValue(numericValue);
            } else {
                // Si no es válido, revertir al valor anterior
                setLocalValue(value);
            }
        }
    };

    // Manejar Enter
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            applyFilter();
        }
    };

    // Manejar pérdida de foco
    const handleBlur = () => {
        applyFilter();
    };

    // Manejar cambio de valor - permitir solo números y punto decimal
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        // Permitir números, punto decimal, y signo negativo
        if (inputValue === '' || /^-?\d*\.?\d*$/.test(inputValue)) {
            setLocalValue(inputValue);
        }
    };

    return (
        <Input
            type="number"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={config?.placeholder || placeholder}
            disabled={disabled}
            data-testid={dataTestId}
            className="h-10"
            step="any" // Permitir decimales
        />
    );
});

NumberInput.displayName = 'NumberInput';

export default NumberInput;
